import { useEffect, useState, type FormEvent } from "react";
import { api } from "../lib/api";
import { useAuth } from "../app/AuthContext";

interface TagRow {
  id: string;
  slug: string;
  name: string;
  type: string;
  description: string | null;
  parent_tag_id: string | null;
}

const TAG_TYPES = [
  "primary_category", "secondary_category", "style_direction",
  "aesthetic", "material", "market", "price_level",
] as const;

export function AdminTagsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<TagRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>("");
  const [editing, setEditing] = useState<TagRow | null>(null);
  const [creating, setCreating] = useState<{ name: string; type: string; description: string; parentTagId: string }>({
    name: "", type: "style_direction", description: "", parentTagId: "",
  });

  const load = () => {
    setLoading(true);
    api.admin.tagsList(filterType || undefined)
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filterType]);

  if (!user || (user.role !== "admin" && user.role !== "super_admin")) {
    return <div className="container empty-state">Admin access required.</div>;
  }

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!creating.name.trim()) return;
    await api.admin.tagCreate({
      name: creating.name.trim(),
      type: creating.type,
      description: creating.description || undefined,
      parentTagId: creating.parentTagId || undefined,
    });
    setCreating({ ...creating, name: "", description: "" });
    load();
  };

  const onSaveEdit = async () => {
    if (!editing) return;
    await api.admin.tagUpdate(editing.id, {
      name: editing.name,
      description: editing.description,
      parent_tag_id: editing.parent_tag_id,
    });
    setEditing(null);
    load();
  };

  const onDelete = async (t: TagRow) => {
    if (!confirm(`Delete tag "${t.name}" and remove from all brands?`)) return;
    await api.admin.tagDelete(t.id);
    load();
  };

  const primaries = items.filter((t) => t.type === "primary_category");

  return (
    <div className="container" style={{ paddingBlock: "2rem", maxWidth: 1080 }}>
      <h2>Tags</h2>

      <form onSubmit={onCreate} className="filter-bar" style={{ marginTop: "1.5rem" }}>
        <input
          placeholder="New tag name"
          value={creating.name}
          onChange={(e) => setCreating({ ...creating, name: e.target.value })}
          style={{ maxWidth: 240 }}
        />
        <select value={creating.type} onChange={(e) => setCreating({ ...creating, type: e.target.value })}>
          {TAG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {creating.type === "secondary_category" && (
          <select value={creating.parentTagId} onChange={(e) => setCreating({ ...creating, parentTagId: e.target.value })}>
            <option value="">— parent —</option>
            {primaries.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <input
          placeholder="Description (optional)"
          value={creating.description}
          onChange={(e) => setCreating({ ...creating, description: e.target.value })}
          style={{ maxWidth: 320 }}
        />
        <button type="submit" className="primary">+ Add</button>
      </form>

      <div className="filter-bar">
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="">All types</option>
          {TAG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {loading ? <div className="empty-state">Loading…</div> : (
        <div className="admin-table-wrap"><table className="admin-table">
          <thead>
            <tr><th>Name</th><th>Slug</th><th>Type</th><th>Parent</th><th>Description</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((t) => {
              const isEditing = editing?.id === t.id;
              return (
                <tr key={t.id}>
                  <td>
                    {isEditing
                      ? <input value={editing!.name} onChange={(e) => setEditing({ ...editing!, name: e.target.value })} />
                      : t.name}
                  </td>
                  <td style={{ color: "var(--fg-subtle)" }}>{t.slug}</td>
                  <td>{t.type}</td>
                  <td>{t.parent_tag_id ? items.find((x) => x.id === t.parent_tag_id)?.name ?? t.parent_tag_id : "—"}</td>
                  <td>
                    {isEditing
                      ? <input value={editing!.description ?? ""} onChange={(e) => setEditing({ ...editing!, description: e.target.value })} />
                      : (t.description ?? "—")}
                  </td>
                  <td>
                    {isEditing ? (
                      <>
                        <button onClick={onSaveEdit} className="primary">Save</button>{" "}
                        <button onClick={() => setEditing(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setEditing(t)}>Edit</button>{" "}
                        <button onClick={() => onDelete(t)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      )}
    </div>
  );
}
